<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
            @per-page-changed="handlePerPageChange"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportTags"
                        />
                    </div>

                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openCreateTab"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('food.carte.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name (colored badge - clickable) -->
            <template #column-name="{ item }">
                <span
                    class="ppv2-inline-block ppv2-px-3 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-opacity-80 ppv2-transition-opacity ppv2-underline"
                >
                    #{{ item.name }}
                </span>
            </template>

            <!-- Column: Subcategories Count -->
            <template #column-foods_count="{ item }">
                <span
                    class="ppv2-inline-block ppv2-px-3 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-opacity-80 ppv2-transition-opacity"
                >
                    {{ item.foods_count }}
                </span>
            </template>

            <!-- Column: Subcategories Count -->
            <template #column-food_categories_count="{ item }">
                <span
                    class="ppv2-inline-block ppv2-px-3 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-opacity-80 ppv2-transition-opacity"
                >
                    {{ item.food_categories_count }}
                </span>
            </template>

            <!-- Column: Printer -->
            <template #column-printer="{ item }">
                <div
                    @click.stop="togglePrinterDropdown(item.id)"
                    class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-transition-all"
                    :ref="'printerDropdown-' + item.id"
                >
                    <span>
                        {{ item.printer ? item.printer.device_name : $t('food.carte.select') }}
                    </span>
                    <ChevronDown class="ppv2-w-4 ppv2-h-4" />
                </div>

                <!-- Printer Dropdown Menu -->
                <div
                    v-if="activePrinterDropdown === item.id"
                    class="menuDropdown ppv2-fixed ppv2-overflow-hidden ppv2-z-[100] ppv2-shadow-xl ppv2-border ppv2-border-gray-100"
                    :style="printerDropdownPosition"
                    :ref="'printerMenu-' + item.id"
                >
                    <div class="ppv2-max-h-[200px] ppv2-overflow-y-auto">
                        <button
                            v-for="print in printersOptions"
                            :key="print.id"
                            @click="changePrinter(item, print)"
                            class="dropdownItem ppv2-w-full text-sm ppv2-text-left ppv2-px-4 ppv2-py-2 poppinsRegular ppv2-transition-colors ppv2-text-slate-600 ppv2-flex ppv2-items-center ppv2-gap-3"
                        >
                            <span :class="{ 'ppv2-pl-4': false }">
                                {{ print.device_name }}
                            </span>
                        </button>
                    </div>
                </div>
            </template>
            <template #column-status="{ item }">
                <button
                    @click="toggleStatus(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.status === 'actif'
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                        "
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                        item.status === 'actif' ? $t('food.status.active') : $t('food.status.inactive')
                    }}</span>
                </button>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEditTab(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.duplicate')"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular ppv2-text-black" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('food.messages.selected_items', { count: selectedCount }) }}
                </span>

                <div class="ppv2-flex ppv2-gap-2">
                    <BaseButton
                        variant="dangerOutline"
                        @click="deleteSelectedItems"
                        customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    >
                        <Trash2 :size="18" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                    </BaseButton>
                </div>
            </template>
        </DataTable>

        <!-- Create/Edit Modal -->
        <TagModal
            v-model="isCreateModalOpen"
            :isEditMode="isEditMode"
            :isSaving="isSaving"
            :tagsForm="tagsForm"
            :printersOptions="printersOptions"
            :errors="formErrors"
            @save="saveTags"
        />

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ deletePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="danger" @click="confirmDelete" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.delete')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Duplicate Confirmation Popup -->
        <CustomDialog v-model="showDuplicatePopup" :defaultTitle="$t('common.duplicate')" height="250px" minHeight="250px">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ duplicatePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDuplicatePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="confirmDuplicate" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Removed Bulk Discount Modal -->
    </div>
</template>
<script>
import { LayoutGrid, ChevronDown, ChevronRight, Plus, SquarePen, CopyPlus, Trash2, Printer, Percent, Loader2 } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import CategoryModal from './CategoryModal.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import TagModal from './TagModal.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Tags',
    components: {
        LayoutGrid,
        ChevronDown,
        ChevronRight,
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        Printer,
        Percent,
        Loader2,
        Search,
        ExportSelect,
        BaseButton,
        CustomDialog,
        DataTable,
        BaseSelect,
        TagModal,
    },
    props: {
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('food_tags_items_per_page', 10),
            sortBy: 'id',
            sortOrder: 'desc',
            selectedItems: [],
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: [],
            totalPages: 1,

            // Modal State
            isCreateModalOpen: false,
            isEditMode: false,
            isSaving: false,
            isExporting: false,

            // Duplicate Popup
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,

            showFilterModal: false,
            advancedFilters: {
                status: 'all',
                tag_ids: [],
            },
            filterData: {
                tags: [],
            },
            isSavingPositions: false,

            // Bulk Action Modals
            showBulkPrinterModal: false,
            bulkPrinterForm: {
                printer_id: null,
            },
            activePrinterDropdown: null,
            // Form Data
            tagsForm: {
                id: null,
                name: '',
                slug: '',
                printer_id: null,
                active: true, // Default to active
            },
            formErrors: {},
            printersOptions: [],
            printerDropdownPosition: { top: '0px', left: '0px', width: 'auto' },
        }
    },
    computed: {
        tableColumns() {
            return [
                { key: 'name', label: this.$t('tags.columns.name'), sortable: true, type: 'text' },
                {
                    key: 'foods_count',
                    label: this.$t('tags.columns.foods_count'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'food_categories_count',
                    label: this.$t('tags.columns.category_count'),
                    sortable: true,
                    type: 'number',
                },
                { key: 'printer', label: this.$t('tags.columns.printer'), sortable: true, type: 'text' },
                { key: 'status', label: this.$t('tags.columns.status'), sortable: true, type: 'badge' },
                { key: 'actions', label: this.$t('tags.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        activeFiltersCount() {
            let count = 0
            if (this.advancedFilters.status !== 'all') count++
            if (this.advancedFilters.tag_ids?.length > 0) count++
            return count
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_tags_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(id) {
            const idx = this.selectedItems.indexOf(id)
            idx > -1 ? this.selectedItems.splice(idx, 1) : this.selectedItems.push(id)
        },
        toggleSelectAll() {
            this.selectedItems = this.selectedItems.length === this.items.length ? [] : this.items.map((i) => i.id)
        },
        async toggleStatus(item) {
            const prev = item.status
            item.status = prev === 'actif' ? 'inactif' : 'actif'
            try {
                await axios.patch('/back-office/v3/food/tags/update-status', {
                    id: item.id,
                    active: item.status === 'actif',
                })
                this.$toast?.success('Statut mis à jour avec succès')
            } catch {
                item.status = prev
                this.$toast?.error('Erreur lors de la mise à jour du statut')
            }
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async openFilterModal() {
            if (this.filterData.tags.length === 0) {
                try {
                    const response = await axios.get('/back-office/v3/food/get-filters-data')
                    this.filterData = response.data.data
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                }
            }
            this.showFilterModal = true
        },
        handleAdvancedFilters(filters) {
            this.advancedFilters = filters
            this.currentPage = 1
            this.fetchData()
        },
        deleteSingleItem(id) {
            this.deleteAction = 'single'
            this.itemToDelete = id
            this.deletePopupDescription = this.$t('tags.actions.confirm_delete_tag')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (!this.selectedItems.length) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('tags.actions.confirm_delete_tags')
            this.showDeletePopup = true
        },
        async confirmDelete() {
            const ids = this.deleteAction === 'single' ? [this.itemToDelete] : this.selectedItems

            // Use unified bulkAction endpoint for delete as well
            try {
                await axios.post('/back-office/v3/food/tags/bulk-action', {
                    ids: ids,
                    action: 'delete',
                })
                this.fetchData()
                this.selectedItems = []
                this.showDeletePopup = false
                this.$toast?.success('Tags supprimés avec succès')
                this.$emit('refresh-stats')
            } catch (e) {
                alert(this.$t('food.messages.delete_error'))
            }
        },

        duplicateItem(itemId) {
            this.itemToDuplicate = itemId
            this.duplicatePopupDescription = 'Êtes-vous sûr de vouloir dupliquer ce tag ?'
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            try {
                const response = await axios.post('/back-office/v3/food/tags/duplicate', { id: this.itemToDuplicate })
                if (response.data.success) {
                    this.$toast?.success('Tag dupliqué avec succès')
                    this.fetchData()
                    this.$emit('refresh-stats')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating category:', error)
                alert('Erreur lors de la duplication')
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },

        // Bulk Action Methods
        openBulkPrinterModal() {
            this.bulkPrinterForm = { printer_id: null }
            this.fetchPrinters()
            this.showBulkPrinterModal = true
        },
        async submitBulkPrinter() {
            if (!this.bulkPrinterForm.printer_id) {
                alert('Veuillez sélectionner une imprimante')
                return
            }
            try {
                await axios.post('/back-office/v3/food/tags/bulk-action', {
                    ids: this.selectedItems,
                    action: 'update_printer',
                    printer_id: this.bulkPrinterForm.printer_id,
                })
                this.showBulkPrinterModal = false
                this.fetchData()
                this.selectedItems = []
                this.$toast?.success('Imprimante mise à jour pour les tags sélectionnés')
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.save_error'))
            }
        },
        openCreateTab() {
            this.isEditMode = false
            this.tagsForm = {
                id: null,
                name: '',
                slug: '',
                printer_id: null,
                active: true,
            }
            this.fetchPrinters()
            this.formErrors = {}
            this.isCreateModalOpen = true
        },

        openEditTab(id) {
            const item = this.items.find((i) => i.id === id)
            if (!item) return
            console.log('item', item)

            this.isEditMode = true
            this.tagsForm = {
                id: item.id,
                name: item.name,
                slug: item.slug,
                printer_id: item.printer ? item.printer.id : null,
                active: item.status === 'actif',
            }
            this.fetchPrinters()
            this.formErrors = {}
            this.isCreateModalOpen = true
        },

        async saveTags() {
            if (!this.tagsForm.name) {
                alert(this.$t('food.messages.name_required'))
                return
            }
            this.formErrors = {}
            this.isSaving = true
            try {
                const url = this.isEditMode ? '/back-office/v3/food/tags/update' : '/back-office/v3/food/tags/store'
                const payload = { ...this.tagsForm }
                await axios.post(url, payload)

                this.isCreateModalOpen = false
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                if (e.response && e.response.status === 422) {
                    this.formErrors = e.response.data.errors
                } else {
                    alert("Une erreur est survenue lors de l'enregistrement.")
                }
            } finally {
                this.isSaving = false
            }
        },

        async fetchData() {
            try {
                const params = {
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                    ...this.advancedFilters,
                }

                const response = await axios.get('/back-office/v3/food/tags/list', { params })
                this.items = response.data.data || []
                this.totalPages = response.data.last_page || 1
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('API error:', error)
            }
        },

        async fetchPrinters() {
            try {
                const response = await axios.get('/back-office/v3/food/printers/list')
                this.printersOptions = response.data.data || []
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('API error:', error)
            }
        },
        async changePrinter(item, printer) {
            this.activePrinterDropdown = null
            try {
                const response = await axios.post('/back-office/v3/food/tags/change-printer', {
                    id: item.id,
                    printer_id: printer.id,
                })
                this.$toast?.success('Imprimante mise à jour avec succès')
                this.fetchData()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('API error:', error)
            }
        },

        // Navigation Methods (Removed breadcrumbs)
        viewProductsForTag(item) {
            // Emit event to parent to switch to products tab filtered by this tag
            this.$emit('view-products', {
                tagId: item.id,
                tagName: item.name,
            })
        },
        async exportTags(format = 'csv') {
            if (this.isExporting) return

            this.isExporting = true
            try {
                const res = await axios.get('/back-office/v3/food/tags/export', {
                    params: { format },
                    responseType: 'blob',
                })

                // Create a link to download the file
                const url = window.URL.createObjectURL(new Blob([res.data]))
                const link = document.createElement('a')
                link.href = url

                // Extract filename from content-disposition if possible
                const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition']
                let fileName = `tags_export_${format}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv'}`
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1] || fileNameMatch[2]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                // Cleanup
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                alert("Une erreur est survenue lors de l'exportation.")
            } finally {
                this.isExporting = false
            }
        },
        togglePrinterDropdown(itemId) {
            if (this.activePrinterDropdown === itemId) {
                this.activePrinterDropdown = null
            } else {
                this.activePrinterDropdown = itemId
                this.$nextTick(() => {
                    this.calculatePrinterDropdownPosition(itemId)
                })
            }
        },
        calculatePrinterDropdownPosition(itemId) {
            const button = this.$refs['printerDropdown-' + itemId]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            const DROPDOWN_WIDTH = 192 // w-48 = 12rem = 192px
            const MARGIN = 8 // safe edge margin in px
            const viewportWidth = window.innerWidth

            // Prefer aligning to the left edge of the button, but clamp so it
            // never overflows right or left edge of the viewport.
            let left = rect.left
            if (left + DROPDOWN_WIDTH > viewportWidth - MARGIN) {
                left = viewportWidth - DROPDOWN_WIDTH - MARGIN
            }
            if (left < MARGIN) {
                left = MARGIN
            }

            this.printerDropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${left}px`,
                width: `${Math.min(DROPDOWN_WIDTH, viewportWidth - MARGIN * 2)}px`,
            }
        },

        updatePrinterDropdownPosition() {
            if (this.activePrinterDropdown !== null) {
                this.calculatePrinterDropdownPosition(this.activePrinterDropdown)
            }
        },
        handleClickOutside(event) {
            if (this.activePrinterDropdown !== null) {
                const btnRef = this.$refs['printerDropdown-' + this.activePrinterDropdown]
                const menuRef = this.$refs['printerMenu-' + this.activePrinterDropdown]

                const btnEl = Array.isArray(btnRef) ? btnRef[0] : btnRef
                const menuEl = Array.isArray(menuRef) ? menuRef[0] : menuRef

                if (btnEl && !btnEl.contains(event.target) && menuEl && !menuEl.contains(event.target)) {
                    this.activePrinterDropdown = null
                }
            }
        },
    },
    mounted() {
        this.fetchData()
        this.fetchPrinters()
        // Emit initial breadcrumb state
        if (this.breadcrumbs) {
            this.$emit('breadcrumb-change', this.breadcrumbs.length > 0)
        }
        window.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updatePrinterDropdownPosition, true)
        window.addEventListener('resize', this.updatePrinterDropdownPosition)
    },
    beforeUnmount() {
        window.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updatePrinterDropdownPosition, true)
        window.removeEventListener('resize', this.updatePrinterDropdownPosition)
    },
}
</script>

<style scoped lang="scss">
.textLink {
    color: #7d2dbd;
    text-decoration: underline;
}
.dark {
    .textLink {
        color: #a855f7;
    }
}
</style>
